import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from './States.tsx';

/**
 * A boundary per region, not per application.
 *
 * A whole-page boundary turns one failed widget into a blank screen. Each
 * region that can fail independently gets its own, so a broken pipeline
 * summary leaves the rest of Home working - which is what the screen spec asks
 * for when it says "per-widget error, never a whole-page failure".
 */
interface Props {
  /** What failed, named in the message the person reads. */
  region: string;
  children: ReactNode;
  onReset?: () => void;
  /**
   * Clears the error when this value changes.
   *
   * A React error boundary latches: once it has caught, it keeps showing the
   * fallback until something resets it. Nothing did. So one screen throwing
   * left "could not be shown" in place across every later navigation, and the
   * whole application looked dead until a hard refresh - Home crashed, and
   * Contacts, which was perfectly healthy, appeared to be broken too.
   *
   * The shell passes the pathname. A person who navigates somewhere else has
   * asked for something else, and is owed an attempt at it.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.region}] render failed`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <ErrorState
        title={`${this.props.region} could not be shown`}
        body="This part of the page failed while rendering. The rest of the screen still works."
        onRetry={() => {
          this.setState({ error: null });
          this.props.onReset?.();
        }}
      />
    );
  }
}
